import * as moment from 'moment';
import * as _ from 'lodash';
import { IListItem, IDonorLogistics, IDonor, ICenter } from '@nkr/models/index';
import { AnatomyMaxPreference } from '@nkr/enums/anatomy-max-preference';

export const Utility = {
    getListItemByName: (name: string, listToSearch: IListItem[]): IListItem => {
        var item = listToSearch.find(i => i.description.toLocaleUpperCase() == name);
        return (item == null ? { id: 0, description: '', abbreviation: '' } : item);
    },

    getClusterStatusByNames: (names: string[], listToSearch: IListItem[]): IListItem[] => {
        var list: IListItem[] = [];

        names.forEach(name => {
            var status = Utility.getListItemByName(name, listToSearch);

            list.push(status);
        })

        return list;
    },

    isInClusterStatusList: (id: number, names: string[], listToSearch: IListItem[]): boolean => {
        var list = Utility.getClusterStatusByNames(names, listToSearch);

        return list.find(item => item.id == id) != null;
    },

    isClusterStatus: (id: number, name: string, listToSearch: IListItem[]): boolean => {
        var item = Utility.getListItemByName(name, listToSearch);

        return item.id == id;
    },

    getChainStatusByName: (name: string, listToSearch: IListItem[]): IListItem => {
        var status = listToSearch.find(cl => cl.description.toUpperCase() == name);

        return (status == null ? { id: 0, description: '', abbreviation: '' } : status);
    },

    getChainStatusByNames: (names: string[], listToSearch: IListItem[]): IListItem[] => {
        var list: IListItem[] = [];

        names.forEach(name => {
            var status = Utility.getChainStatusByName(name, listToSearch);

            list.push(status);
        })

        return list;
    },

    isInChainStatusList: (id: number, names: string[], listToSearch: IListItem[]): boolean => {
        var list = Utility.getChainStatusByNames(names, listToSearch);

        return list.find(item => item.id == id) != null;
    },

    isChainStatus: (id: number, name: string, listToSearch: IListItem[]): boolean => {
        var item = Utility.getChainStatusByName(name, listToSearch);

        return item.id == id;
    },

    getMapButtonStyle: (logistics: IDonorLogistics): string => {
        var mapButtonStyle = 'default';

        if (logistics != null) {
            if (logistics.transportMethod.toUpperCase() == 'COMMOBC') {
                mapButtonStyle = 'danger'
            } else {
                if (logistics.hasTravelWarning) {
                    if (logistics.hasCommercialTravelWarning) {
                        mapButtonStyle = 'info'
                    } else {
                        mapButtonStyle = 'warning'
                    }
                }
            }
        }

        return mapButtonStyle;
    },

    checkForDST: (t: Date): boolean => {
        var jan = new Date(t.getFullYear(), 0, 1);
        var jul = new Date(t.getFullYear(), 6, 1);

        return Math.min(jan.getTimezoneOffset(), jul.getTimezoneOffset()) == t.getTimezoneOffset();
    },

    getTimeZoneOffset: (): number => {
        return new Date().getTimezoneOffset() + (Utility.checkForDST(new Date()) ? 60 : 0);
    },

    isNumber: (n: any) => {
        return !isNaN(parseFloat(n)) && isFinite(n);
    },

    getOrdinalSuffixOf: (i: number, includeValue: boolean) => {
        var j = i % 10,
            k = i % 100;

        if (j == 1 && k != 11) {
            if (includeValue) {
                return i + 'st';
            } else {
                return 'st';
            }
        }

        if (j == 2 && k != 12) {
            if (includeValue) {
                return i + 'nd';
            } else {
                return 'nd';
            }
        }

        if (j == 3 && k != 13) {
            if (includeValue) {
                return i + 'rd';
            } else {
                return 'rd';
            }
        }

        if (includeValue) {
            return i + 'th';
        } else {
            return 'th';
        }
    },

    getOrdinalSuffixOfDate: (x: Date, includeTime: boolean = false) => {
        let day = moment(x).clone().date();
        let beginningString = moment(x).clone().format('MMMM');
        let endingString = moment(x).clone().format('YYYY');

        if (includeTime) {
            endingString = moment(x).clone().format('YYYY h:mm A');
        }

        var firstList = [1, 21, 31];
        var secondList = [2, 22];
        var thirdList = [3, 23];

        var middleString = day + 'th';

        if (firstList.includes(day)) {
            middleString = day + "st";
        }

        if (secondList.includes(day)) {
            middleString = day + "nd";
        }

        if (thirdList.includes(day)) {
            middleString = day + "rd";
        }

        return beginningString + ' ' + middleString + ', ' + endingString;
    },

    dateComparator: (date1: string, date2: string, nodeA: any, nodeB: any, isInverted: boolean, format?: string) => {
        let f = format != null ? format : 'YYYYMMDD';

        var date1Number = parseInt(moment(date1).format(f));
        var date2Number = parseInt(moment(date2).format(f));

        let isDate1Valid = !isNaN(date1Number);
        let isDate2Valid = !isNaN(date2Number);

        if (!isDate1Valid && !isDate2Valid) {
            return 0;
        }

        if (!isDate1Valid) {
            return isInverted ? -1 : 1;
        }

        if (!isDate2Valid) {
            return isInverted ? 1 : -1;
        }

        return date1Number - date2Number;
    },

    nameComparator: (name1: string, name2: string, nodeA: any, nodeB: any, isInverted: boolean, format?: string) => {
        return name1.localeCompare(name2, undefined, { sensitivity: 'base' });
    },

    decimalComparator: (value1: any, value2: any, nodeA: any, nodeB: any, isInverted: boolean, format?: string) => {
        if (!Utility.isNumber(value1) && !Utility.isNumber(value2)) {
            return 0;
        }

        if (!Utility.isNumber(value1)) {
            return isInverted ? -1 : 1;
        }

        if (!Utility.isNumber(value2)) {
            return isInverted ? 1 : -1;
        }

        let number1 = parseFloat(value1);
        let number2 = parseFloat(value2);

        return number1 - number2;
    },

    getWaitMessage: () => {
        let messages = ['Un momento, por favor', 'Un moment, s’il vous plaît', 'Einen Moment, bitte', 'Um momento, por favor'];

        return messages[Math.floor(Math.random() * messages.length)]
    },

    isValidEmail: (email: string) => {
        let re = /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    },

    isNotNullOrBlank: (data: string) => {
        if (data == null) { return false; }
        if (data.trim() == '') { return false; }
        return true;
    },

    realBloodType: (inAbo: string, inASubType: number, inABSubType: number) => {
        if (inAbo == "A" && inASubType == ASubTypes.A1) {
            return "A1";
        } else if (inAbo == "A" && inASubType == ASubTypes.NonA1) {
            return "A2";
        } else if (inAbo == "A" && inASubType == ASubTypes.Unknown) {
            return "A?";
        } else if (inAbo == "AB" && inABSubType == ABSubTypes.AB1) {
            return "A1B";
        } else if (inAbo == "AB" && inABSubType == ABSubTypes.NonAB1) {
            return "A2B";
        } else if (inAbo == "AB" && inABSubType == ABSubTypes.Unknown) {
            return "A?B";
        } else {
            return inAbo;
        }
    },

    getRange: (startDate, endDate, type) => {
        let fromDate = moment(startDate);
        let toDate = moment(endDate);
        let diff = toDate.diff(fromDate, type);
        let range: moment.Moment[] = [];

        for (let i = 0; i < diff; i++) {
            range.push(moment(startDate).add(i, type))
        }

        return _.reverse(range);
    },

    roundNumber: (value: number, decimal_digit: number) => {
        let powerOften = Math.pow(10, decimal_digit);
        let result = Math.round(value * powerOften) / powerOften;
        return result;
    },

    formatUsDollar: (value: number, decimalPlaces: number) => {
        const USDollar = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: decimalPlaces
        });

        return USDollar.format(value);
    },

    donorMeetsAnatomyPref: (donor: IDonor, center: ICenter) => {
        if (donor.whichKidney.toUpperCase().indexOf('RIGHT') >= 0 || donor.whichKidney.toUpperCase().indexOf('R-OPT') >= 0) {
            if (center.anatomyPrefArteriesRight != -1 && center.anatomyPrefVeinsRight != -1
                && (center.anatomyPrefArteriesRight == AnatomyMaxPreference.Any || center.anatomyPrefArteriesRight >= donor.numberOfArteries)
                && (center.anatomyPrefVeinsRight == AnatomyMaxPreference.Any || center.anatomyPrefVeinsRight >= donor.numberOfVeins)) {
                return true;
            }
        } else {
            if ((center.anatomyPrefArteriesLeft == AnatomyMaxPreference.Any || center.anatomyPrefArteriesLeft >= donor.numberOfArteries)
                && (center.anatomyPrefVeinsLeft == AnatomyMaxPreference.Any || center.anatomyPrefVeinsLeft >= donor.numberOfVeins)) {
                return true;
            }
        }

        return false;
    },

    uuidv4: () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
            .replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0,
                    v = c == 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
    },

    percent: (num: number, denom: number) => {
        if (_.isNull(num) || denom == 0) return '--';

        return _.round((num / denom) * 100, 0).toString() + '%';
    },

    stringSortComparator: (valueA: string, valueB: string) => {
        if (!valueA) return -1;
        if (!valueB) return 1;

        return valueA.trim().toLowerCase().localeCompare(valueB.trim().toLocaleLowerCase());
    },

    shortenString: (str: string, n: number): string | null | undefined => {
        if (str) {
            return (str.length > n) ? str.slice(0, n - 1) + '...' : str;
        } else {
            return str;
        }
    },

    getFirstOfCurrentMonth: () => {
        const date = new Date();
        const year = date.getFullYear();
        // `getMonth()` returns a zero-indexed month (0-11).
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        // `yyyy-mm-dd` format requires a leading zero for single-digit months.
        return moment(`${year}-${month}-01`).toDate();
    },

    getLastOfCurrentMonth: () => {
        // Get the current date
        const now = new Date();

        // Create a new Date object for the next month, setting the day to 0.
        // JavaScript's Date object automatically handles this to return the
        // last day of the *previous* month.
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        return lastDay;
    },

    getDateInputStr: (date?: Date) => {
        return date ? moment(date).format('YYYY-MM-DD') : '';
    },

    addBusinessDays: (datePassed: moment.MomentInput, xAmountDays: number) => {
        let date = moment(datePassed).startOf('day');
        let addedDays = 0;

        while (addedDays < xAmountDays) {
            const day = date.day();

            if (day !== 0 && day !== 6) {
                addedDays++;
            }

            date.add(1, 'day');
        }
        // step back 1 because loop always advances one extra day
        date.subtract(1, 'day');
        // ensure final result is not weekend
        while (date.day() === 0 || date.day() === 6) {
            date.add(1, 'day');
        }
        return date;
    },

    addBusinessDaysFormatted: (datePassed: moment.MomentInput, xAmountDays: number) => {
        const result = Utility.addBusinessDays(datePassed, xAmountDays);
        return result.format('MM/DD/YYYY');
    },

    validatePhoneNumber: (phone: string, label: string): { isValid: boolean, errorMessage: string } => {
        if (!phone || phone.trim() === '') {
            return { isValid: true, errorMessage: '' }; // Phone numbers are optional
        }

        // Remove all non-numeric characters for length check
        let cleaned = phone.replace(/\D/g, '');

        if (cleaned.length < 10) {
            return { isValid: false, errorMessage: `${label} must be at least 10 digits` };
        }

        if (cleaned.length > 15) {
            return { isValid: false, errorMessage: `${label} must be no more than 15 digits` };
        }

        // First check if it's in our preferred format (XXX) XXX-XXXX
        let preferredPattern = /^\(\d{3}\) \d{3}-\d{4}$/;

        if (preferredPattern.test(phone)) {
            return { isValid: true, errorMessage: '' };
        }

        // Fallback to backend PhoneValidation.cs pattern for compatibility
        // Pattern: ^[01]?[- .]?\(?[2-9]\d{2}\)?[- .]?\d{3}[- .]?\d{4}$
        let backendPattern = /^[01]?[- .]?\(?[2-9]\d{2}\)?[- .]?\d{3}[- .]?\d{4}$/;

        if (!backendPattern.test(phone)) {
            // Try to be helpful - if we have 10 digits, suggest they use the correct format
            if (cleaned.length === 10) {
                return { isValid: false, errorMessage: `For ${label} please use format (123) 456-7890` };
            }

            return { isValid: false, errorMessage: `${label} has an invalid phone number format. Use format like (123) 456-7890` };
        }

        return { isValid: true, errorMessage: '' };
    },
    formatPhone: (phone: string): string => {
        return /^\d{10}$/.test(phone) ? phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : phone;
    },
    getRootDomain: (url: string) => {
        try {
            const parts = new URL(url).hostname.split('.');
            // Remove 'www.' if it exists at the beginning of the string
            return parts.slice(-2).join('.');
        } catch (error) {
            console.error("Invalid URL:", error);
            return null;
        }
    },

    normalizePhoneNumber: (phone: string | null): string => {
        if (!phone || phone.trim() === '') return '';

        // Remove all non-numeric characters
        let cleaned = phone.replace(/\D/g, '');

        // Handle different legacy formats
        if (cleaned.length === 10) {
            // Standard 10-digit format: 1234567890 -> (123) 456-7890
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
            // 11-digit with country code: 11234567890 -> (123) 456-7890
            let withoutCountryCode = cleaned.slice(1);

            return `(${withoutCountryCode.slice(0, 3)}) ${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6)}`;
        }

        // If phone already appears to be in correct format, return as-is
        let phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;

        if (phonePattern.test(phone)) {
            return phone;
        }

        if (cleaned.length >= 10) {
            let tenDigits = cleaned.slice(-10); // Take last 10 digits

            return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
        }

        return phone;
    }
}

const ASubTypes = {
    Unknown: 1,
    A1: 2,
    NonA1: 3
}

const ABSubTypes = {
    Unknown: 1,
    AB1: 2,
    NonAB1: 3
}